import React, { useContext, useEffect, useMemo, useState } from 'react';
import SEO from '../../components/SEO/SEO';
import { ClerkLoaded, SignIn } from "@clerk/clerk-react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import style from './Login.module.css';
import AppLoader from '../../components/AppLoader/AppLoader';
import { useAuthUser } from '../../hooks/useAuthUser';
import { AppAuthContext } from '../../contexts/AppAuthContext';
import { requestWithApiFallback } from '../../utils/apiRequest';
import { buildAuthCallbackRedirectUrl, resolveAuthDestinationFromLocation } from '../../utils/authRedirect';

const Login = () => {
	const API_URL = process.env.REACT_APP_API_URL;
	const { user, isLoaded } = useAuthUser();
	const { isTestAuthEnabled, setTestAuthSession } = useContext(AppAuthContext);
	const navigate = useNavigate();
	const location = useLocation();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [forceHostedAuth, setForceHostedAuth] = useState(false);

	const finalDestination = useMemo(() => {
		return resolveAuthDestinationFromLocation(location);
	}, [location]);

	const callbackRedirectUrl = useMemo(
		() => buildAuthCallbackRedirectUrl(finalDestination),
		[finalDestination]
	);

	const isForcedTestRoute = useMemo(
		() => location.pathname === '/test-signin' || location.pathname === '/test-sign-in',
		[location.pathname]
	);
	const useTestAuthUI = (isTestAuthEnabled || isForcedTestRoute) && !forceHostedAuth;
	const signupLink = useMemo(
		() => `${useTestAuthUI ? '/test-signup' : '/signup'}?redirectTo=${encodeURIComponent(finalDestination)}`,
		[finalDestination, useTestAuthUI]
	);

	useEffect(() => {
		if (user) {
			navigate(finalDestination, { replace: true });
		}
	}, [user, navigate, finalDestination]);

	const handleTestSignIn = async (event) => {
		event.preventDefault();
		setErrorMessage('');

		if (!email.trim() || !password) {
			setErrorMessage('Email and password are required.');
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await requestWithApiFallback(API_URL, (base) => axios.post(
				`${base}/test-signin`,
				{ email: email.trim(), password }
			));

			setTestAuthSession({
				token: response.data.token,
				user: response.data.user,
				signedInAt: new Date().toISOString()
			});
			navigate(finalDestination, { replace: true });
		} catch (error) {
			if (error?.response?.status === 404) {
				setForceHostedAuth(true);
				setErrorMessage('');
				return;
			}
			setErrorMessage(error?.response?.data?.error || 'Could not sign in right now.');
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isLoaded) {
		return <AppLoader message='Preparing sign in...' />;
	}

	if (user) {
		return <AppLoader message='Redirecting...' />;
	}

	if (useTestAuthUI) {
		return (
			<div className={style.LoginPage}>
				<SEO title="Log in" description="Access your Calcium account to continue your fitness journey." path="/login" noIndex />
				<div className={style.AuthShell}>
					<div className={style.AuthHeader}>
						<h1 className={style.AuthTitle}>Welcome Back</h1>
						<p className={style.AuthSubtitle}>Sign in to continue building momentum in Calcium.</p>
					</div>

					<form className={style.TestAuthForm} onSubmit={handleTestSignIn}>
						<label className={style.AuthLabel} htmlFor='test-auth-email'>
							Email
						</label>
						<input
							id='test-auth-email'
							className={style.AuthInput}
							type='email'
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							placeholder='you@example.com'
							autoComplete='email'
						/>

						<label className={style.AuthLabel} htmlFor='test-auth-password'>
							Password
						</label>
						<input
							id='test-auth-password'
							className={style.AuthInput}
							type='password'
							value={password}
							onChange={(event) => setPassword(event.target.value)}
							placeholder='Enter your password'
							autoComplete='current-password'
						/>

						{errorMessage ? <p className={style.AuthError}>{errorMessage}</p> : null}

						<button className={style.AuthPrimaryButton} type='submit' disabled={isSubmitting}>
							{isSubmitting ? 'Signing in...' : 'Sign in'}
						</button>
					</form>

					<p className={style.AuthFooterText}>
						New here? <Link to={signupLink} className={style.AuthFooterLink}>Create an account</Link>
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className={style.LoginPage}>
			<SEO title="Log in" description="Access your Calcium account to continue your fitness journey." path="/login" noIndex />
			<div className={style.LoginForm}>
				<ClerkLoaded>
					{/*
					  redirectUrl is the OAuth/deep-link callback route.
					  afterSignInUrl is the final destination after auth completes.
					*/}
					<SignIn
						redirectUrl={callbackRedirectUrl}
						afterSignInUrl={finalDestination}
					/>
				</ClerkLoaded>
			</div>
		</div>
	)
}

export default Login;